import React, { useEffect, useState } from 'react'
import { Spin, Empty, Tag } from 'antd'
import styled from 'styled-components'
import { getScheduleAssignments } from '../../services/assignmentActivityAPI'

/* ───────── Theme tokens ───────── */
const T = {
  primary: '#3249D7',
  textPrimary: '#242E39',
  textSecondary: '#637487',
  border: '#EBECEE',
  green: '#4cb187',
  amber: '#B5641E',
  red: '#E5484D',
  grey95: '#F2F3F5',
}

const TRIGGER_LABEL = {
  schedule_create: 'Diagnosis (Assessment #1 & #2)',
  schedule_run: 'Main assessment',
  manual: 'Manual assignment',
}
const STATE_COLOR = {
  completed: T.green,
  completed_with_errors: T.amber,
  failed: T.red,
}

const List = styled.div`display: flex; flex-direction: column; gap: 10px;`
const Row = styled.button`
  text-align: left; cursor: pointer; background: ${T.grey95}; border: 1px solid ${T.border};
  border-radius: 12px; padding: 14px 16px; display: flex; align-items: center; gap: 16px;
  transition: border-color .15s; width: 100%;
  &:hover { border-color: ${T.primary}; }
`
const Dot = styled.span`width: 9px; height: 9px; border-radius: 50%; background: ${(p) => p.$c}; flex-shrink: 0;`
const Main = styled.div`flex: 1;`
const Label = styled.div`font-size: 14px; font-weight: 600; color: ${T.textPrimary};`
const Meta = styled.div`font-size: 12px; color: ${T.textSecondary}; margin-top: 2px;`

/**
 * Timeline of assignment jobs for one schedule (diagnosis job at creation +
 * main run jobs later). Clicking a row opens that job's Activity view.
 * Props: { scheduleId, onOpenJob(jobId) }
 */
const ScheduleAssignmentsTimeline = ({ scheduleId, onOpenJob }) => {
  const [loading, setLoading] = useState(true)
  const [jobs, setJobs] = useState([])

  useEffect(() => {
    let active = true
    if (!scheduleId) return undefined
    getScheduleAssignments(scheduleId)
      .then((res) => { if (active) setJobs(res.data?.data?.jobs || []) })
      .catch(() => { if (active) setJobs([]) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [scheduleId])

  if (loading) return <Spin style={{ display: 'block', margin: '40px auto' }} />
  if (!jobs.length) return <Empty description="No assignment runs yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />

  return (
    <List>
      {jobs.map((j) => {
        const color = STATE_COLOR[j.state] || T.primary
        return (
          <Row key={j.id} onClick={() => onOpenJob && onOpenJob(j.id)}>
            <Dot $c={color} />
            <Main>
              <Label>{TRIGGER_LABEL[j.trigger_source] || j.trigger_source}</Label>
              <Meta>
                {j.assigned_count}/{j.total_count} assigned
                {j.failed_count > 0 ? ` · ${j.failed_count} failed` : ''}
                {' · '}{new Date(j.created_at).toLocaleString()}
              </Meta>
            </Main>
            <Tag color={j.failed_count > 0 ? 'orange' : j.state === 'completed' ? 'green' : 'blue'}>
              {String(j.state).replace(/_/g, ' ')}
            </Tag>
          </Row>
        )
      })}
    </List>
  )
}

export default ScheduleAssignmentsTimeline
